import { useState, useRef } from 'react';
import './CsvDebug.css';
import { getAccessToken } from '../utils/auth';
import { processCsvDebug } from '../utils/httpApi';
import { isApiSuccess, getFirstApiError } from '../utils/api';

function CsvDebug({ receivers = [] }) {
  const [selectedReceiver, setSelectedReceiver] = useState('');
  const [csvFile, setCsvFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [expandedAircraft, setExpandedAircraft] = useState(null);
  const [expandedDebugLogs, setExpandedDebugLogs] = useState({});
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file);
      setError(null);
    }
  };

  const handleProcess = async () => {
    if (!selectedReceiver || !csvFile) {
      setError('Please select a receiver and upload a CSV file.');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);
    setExpandedAircraft(null);

    try {
      // Read file and convert to Base64
      const fileContent = await csvFile.text();
      const csvDataBase64 = btoa(unescape(encodeURIComponent(fileContent)));

      const token = getAccessToken();
      const response = await processCsvDebug(selectedReceiver, csvDataBase64, token);

      if (isApiSuccess(response)) {
        setResult(response);
      } else {
        setError(getFirstApiError(response) || 'Failed to process CSV data.');
      }
    } catch (err) {
      console.error('Error processing CSV:', err);
      setError('Failed to process CSV data. ' + (err.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const toggleAircraft = (icao) => {
    setExpandedAircraft(expandedAircraft === icao ? null : icao);
  };

  const toggleDebugLog = (key) => {
    setExpandedDebugLogs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatTimeDelta = (ms) => {
    if (ms == null) return '-';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'VALIDATED': return 'validated';
      case 'UNVALIDATED': return 'unvalidated';
      case 'REJECTED': return 'rejected';
      case 'DECODE_FAILED': return 'decode-failed';
      default: return 'not-position';
    }
  };

  const getMessageRowClass = (status) => {
    switch (status) {
      case 'VALIDATED': return 'msg-validated';
      case 'UNVALIDATED': return 'msg-unvalidated';
      case 'REJECTED': return 'msg-rejected';
      default: return '';
    }
  };

  const formatCpr = (msg) => {
    if (msg.cprFormat == null) return '-';
    const label = msg.cprFormat === 0 ? 'Even' : 'Odd';
    return `${label} ${msg.latCpr}/${msg.lonCpr}`;
  };

  const formatDecode = (msg) => {
    if (msg.decoded) {
      return `${msg.decodedLat.toFixed(4)}, ${msg.decodedLon.toFixed(4)}`;
    }
    if (msg.decodeError) return msg.decodeError;
    return '-';
  };

  const formatDetails = (msg) => {
    const parts = [];
    if (msg.altitude != null) parts.push(`${msg.altitude}ft`);
    if (msg.velocity != null) parts.push(`${msg.velocity}kt`);
    if (msg.callsign) parts.push(msg.callsign);
    if (msg.heading != null) parts.push(`hdg ${msg.heading}`);
    return parts.join(', ') || '-';
  };

  return (
    <div className="csv-debug-container">
      {/* Controls */}
      <div className="csv-debug-controls">
        <select
          value={selectedReceiver}
          onChange={(e) => setSelectedReceiver(e.target.value)}
        >
          <option value="">Select Receiver...</option>
          {receivers.map(r => (
            <option key={r.receiverUuid} value={r.receiverUuid}>
              {r.receiverName} ({r.airportIcao || 'N/A'})
            </option>
          ))}
        </select>

        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          ref={fileInputRef}
        />

        <button
          className="csv-debug-process-btn"
          onClick={handleProcess}
          disabled={loading || !selectedReceiver || !csvFile}
        >
          {loading ? 'Processing...' : 'Process'}
        </button>
      </div>

      {error && <div className="csv-debug-error">{error}</div>}

      {loading && <div className="csv-debug-loading">Processing CSV data through validation pipeline...</div>}

      {result && result.summary && (
        <>
          {/* Receiver info */}
          <div className="csv-debug-receiver-info">
            <span>Receiver: <strong>{result.receiverName}</strong></span>
            <span>Location: {result.receiverLat?.toFixed(4)}, {result.receiverLon?.toFixed(4)}</span>
          </div>

          {/* Summary stats */}
          <div className="csv-debug-summary">
            <div className="csv-debug-stat">
              <span className="csv-debug-stat-value">{result.summary.totalMessages}</span>
              <span className="csv-debug-stat-label">Messages</span>
            </div>
            <div className="csv-debug-stat">
              <span className="csv-debug-stat-value">{result.summary.aircraftCount}</span>
              <span className="csv-debug-stat-label">Aircraft</span>
            </div>
            <div className="csv-debug-stat">
              <span className="csv-debug-stat-value">{result.summary.positionsDecoded}</span>
              <span className="csv-debug-stat-label">Decoded</span>
            </div>
            <div className="csv-debug-stat">
              <span className="csv-debug-stat-value validated">{result.summary.positionsValidated}</span>
              <span className="csv-debug-stat-label">Validated</span>
            </div>
            <div className="csv-debug-stat">
              <span className="csv-debug-stat-value unvalidated">{result.summary.positionsUnvalidated}</span>
              <span className="csv-debug-stat-label">Unvalidated</span>
            </div>
            <div className="csv-debug-stat">
              <span className="csv-debug-stat-value rejected">{result.summary.positionsRejected}</span>
              <span className="csv-debug-stat-label">Rejected</span>
            </div>
          </div>

          {/* Aircraft table */}
          <div className="admin-table-container">
            <table className="csv-debug-aircraft-table">
              <thead>
                <tr>
                  <th style={{ width: '28px' }}></th>
                  <th>ICAO</th>
                  <th>Callsign</th>
                  <th className="num">Messages</th>
                  <th className="num">Positions</th>
                  <th className="num">Validated</th>
                  <th className="num">Unvalidated</th>
                  <th className="num">Rejected</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {result.aircraft && result.aircraft.map(ac => {
                  const isExpanded = expandedAircraft === ac.icaoAddress;
                  return (
                    <AircraftRow
                      key={ac.icaoAddress}
                      ac={ac}
                      isExpanded={isExpanded}
                      onToggle={() => toggleAircraft(ac.icaoAddress)}
                      expandedDebugLogs={expandedDebugLogs}
                      onToggleDebugLog={toggleDebugLog}
                      getStatusClass={getStatusClass}
                      getMessageRowClass={getMessageRowClass}
                      formatTimeDelta={formatTimeDelta}
                      formatCpr={formatCpr}
                      formatDecode={formatDecode}
                      formatDetails={formatDetails}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function AircraftRow({
  ac, isExpanded, onToggle, expandedDebugLogs, onToggleDebugLog,
  getStatusClass, getMessageRowClass, formatTimeDelta, formatCpr, formatDecode, formatDetails
}) {
  return (
    <>
      <tr
        className={`csv-debug-aircraft-row ${isExpanded ? 'expanded' : ''}`}
        onClick={onToggle}
      >
        <td>
          <span className={`csv-debug-expand-icon ${isExpanded ? 'open' : ''}`}>{'\u25B6'}</span>
        </td>
        <td className="csv-debug-icao">
          {ac.icaoAddress}
          {ac.hasSurfacePositions && !isExpanded && (
            <>
              {' '}
              <span className="csv-surface-indicator">
                <span className="surface-dot"></span>
                SFC
              </span>
            </>
          )}
        </td>
        <td>{ac.callsign || '-'}</td>
        <td className="num">{ac.totalMessages}</td>
        <td className="num">{ac.positionMessages}</td>
        <td className="num">{ac.validatedCount}</td>
        <td className="num">{ac.unvalidatedCount}</td>
        <td className="num">{ac.rejectedCount}</td>
        <td>
          {ac.lastValidationStatus && (
            <span className={`csv-validation-badge ${getStatusClass(ac.lastValidationStatus)}`}>
              {ac.lastValidationStatus}
            </span>
          )}
        </td>
      </tr>

      {isExpanded && ac.messages && (
        <tr className="csv-debug-timeline-row">
          <td colSpan="9">
            {/* Aircraft detail header */}
            <div className="csv-debug-ac-header">
              <span className="csv-debug-ac-header-icao">{ac.icaoAddress}</span>
              {ac.callsign && <span className="csv-debug-ac-header-callsign">{ac.callsign}</span>}
              {ac.hasSurfacePositions && (
                <span className="csv-surface-indicator" style={{ background: 'rgba(255,255,255,0.15)' }}>
                  <span className="surface-dot"></span>
                  SFC {ac.surfacePositionCount}
                </span>
              )}
              <div className="csv-debug-ac-header-meta">
                {ac.lastAltitude != null && <span>{ac.lastAltitude} ft</span>}
                {ac.lastVelocity != null && <span>{ac.lastVelocity} kt</span>}
                {ac.lastHeading != null && <span>hdg {ac.lastHeading}</span>}
              </div>
            </div>

            <div className="csv-debug-timeline-container">
              <table className="csv-debug-timeline-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Time</th>
                    <th>Dt</th>
                    <th>Type</th>
                    <th>Details</th>
                    <th>CPR</th>
                    <th>Decode</th>
                    <th>Hex Message</th>
                    <th>Validation</th>
                    <th>Debug</th>
                  </tr>
                </thead>
                <tbody>
                  {ac.messages.map((msg, idx) => {
                    const logKey = `${ac.icaoAddress}-${idx}`;
                    const isLogExpanded = expandedDebugLogs[logKey];

                    return (
                      <tr key={idx} className={getMessageRowClass(msg.status)}>
                        <td>{idx + 1}</td>
                        <td className="time-cell">
                          {msg.timestamp ? msg.timestamp.split(' ')[1] || msg.timestamp : '-'}
                        </td>
                        <td className="delta-cell">{formatTimeDelta(msg.timeDeltaMs)}</td>
                        <td className="type-cell">{msg.msgType || '-'}</td>
                        <td style={{ fontSize: '10px' }}>{formatDetails(msg)}</td>
                        <td className="mono">{formatCpr(msg)}</td>
                        <td style={{ fontSize: '10px' }}>{formatDecode(msg)}</td>
                        <td className="hex-cell" title={msg.hexMessage || ''}>
                          {msg.hexMessage || '-'}
                        </td>
                        <td>
                          <span className={`csv-validation-badge ${getStatusClass(msg.status)}`}>
                            {msg.status || '-'}
                          </span>
                        </td>
                        <td>
                          {msg.debugLog && msg.debugLog.length > 0 && (
                            <div>
                              <span
                                className="csv-debug-log-toggle"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onToggleDebugLog(logKey);
                                }}
                              >
                                {isLogExpanded ? 'Hide' : 'Show'} ({msg.debugLog.length})
                              </span>
                              {isLogExpanded && (
                                <div className="csv-debug-log-content">
                                  {msg.debugLog.join('\n')}
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default CsvDebug;
